import { ButtonCus } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { HONORIFIC_TITLE_SELECT } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { DOWNLOAD_TYPE, DownloadType, HONORIFIC_TITLE_TYPE } from '@/types/enums';
import { ReservationIssuseForm } from '@/types/reservation';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface IssueInvoiceFormProps {
    idModal?: string,
    adjust: ReservationIssuseForm,
    type?: DownloadType,
    isAdmin: boolean
}

export default observer(function IssueInvoiceFormRentalHistory({ idModal, adjust, type = DOWNLOAD_TYPE.RECEIPT, isAdmin }: IssueInvoiceFormProps) {
    // hook
    const { t } = useTranslation();

    // store
    const {
        commissionMonthStore: {
            getSalutation, upateSalutation
        },
        reservationStore: { downloadInvoice, downloadReceipt },
        modalStore: { hideAllModals }
    } = useStore();

    // form
    const validateSchema = yup.object().shape({
        salutationTitle: yup
            .string()
            .trim(),
        salutationType: yup
            .string()
            .required('form_error_validate.required')

    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            salutationTitle: '',
            salutationType: HONORIFIC_TITLE_TYPE.PERSONAL
        }
    });

    const [disableInput, setDisableInput] = useState<boolean>(false);

    // lifecycle
    useEffect(() => {
        onFetch(adjust);
    }, [adjust]);

    const onFetch = async (adjust: any) => {
        if (adjust?.renter?.id) {
            const res = await flowResult(getSalutation({
                userId: Number(adjust?.renter?.id),
                orderId: adjust.id
            }));
            if (res) {
                const { salutationTitle, salutationType, isChange } = res;
                form.reset({
                    salutationTitle: salutationTitle || '',
                    salutationType: salutationType || HONORIFIC_TITLE_TYPE.PERSONAL
                });
                if (salutationTitle && salutationType && !isAdmin && !isChange) {
                    setDisableInput(true);
                }
            }
        }
    };

    // function
    const onSubmit = async (data: FormData) => {
        const payload = {
            ...data,
            ownerId: adjust?.renter?.id || '',
            orderId: adjust.id
        };
        const res = await flowResult(upateSalutation(payload));
        if (res) {
            if (type === DOWNLOAD_TYPE.RECEIPT) {
                await flowResult(downloadReceipt({ id: adjust.id }));
            }

            if (type === DOWNLOAD_TYPE.INVOICE) {
                await flowResult(downloadInvoice({ id: adjust.id }));
            }

            hideAllModals();
        }
    };

    const onCancel = () => {
        hideAllModals();
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='max-h-[75vh] overflow-y-auto'>
                <div className='grid grid-cols-1 xsm:grid-cols-2 gap-4'>
                    <FormField
                        control={form.control}
                        name='salutationTitle'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t(`field.issue_invoice.${type === DOWNLOAD_TYPE.INVOICE ? 'invoice' : 'receipt_2'}_addressee`)}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('placeholder.enter')}
                                        disabled={disableInput}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='salutationType'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('field.issue_invoice.honorific_title')}</FormLabel>
                                <FormControl>
                                    <Combobox
                                        value={field.value?.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.code);
                                        }}
                                        items={HONORIFIC_TITLE_SELECT.map(item => ({
                                            label: t(item.name),
                                            value: item?.value?.toString(),
                                            code: item?.value
                                        }))}
                                        disabled={disableInput}
                                        search={false}
                                        disableClearIcon
                                        required
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <div className='mt-6'>
                    {
                        type === DOWNLOAD_TYPE.INVOICE ?
                            (
                                <>
                                    請求書の宛名が空白の場合、ユーザー名が自動で入力されます。
                                    <br />
                                    会社名などを宛名としてご希望の場合は、宛名をご入力下さい。
                                    {!isAdmin && (
                                        <>
                                            <br />
                                            なお、一度入力した情報は変更することは出来ません。予めご注意下さい。
                                        </>
                                    )}
                                </>
                            ) :
                            (
                                <>
                                    領収書の宛名が空白の場合、ユーザー名が自動で入力されます。
                                    <br />
                                    会社名などを宛名としてご希望の場合は、宛名をご入力下さい。
                                    {!isAdmin && (
                                        <>
                                            <br />
                                            なお、一度入力した情報は変更することは出来ません。予めご注意下さい。
                                        </>
                                    )}
                                </>
                            )
                    }

                </div>

                <div className='flex xsm:flex-row flex-col gap-4 mt-6 justify-center'>
                    <ButtonCus className='w-full md:w-[144px]' variant='outline' size='sm' type='button' onClick={onCancel}>
                        {t('button.cancel')}
                    </ButtonCus>
                    <ButtonCus className='w-full md:w-[144px]' variant='default' size='sm' type='submit'>
                        {type === DOWNLOAD_TYPE.INVOICE ? t('button.issue_invoice') : t('button.issue_receipt')}
                    </ButtonCus>
                </div>
            </form>
        </Form>
    );
});
